'use strict';

import { useLocation } from '@reach/router';
import { useContext, useState } from 'react';

import { ChannelContext } from '../context/channel';
import { StoreContext } from '../context/store';
import { createEvent } from '../utils/tracking';
import useProduct from './use_product';
import useToast from './use_toast';
import useTranslation from './use_translation';

function useItemButton({ onAddToCart, product_id, tracking } = {}) {
	const { addItem, isItemInCart, removeItem } = useContext(StoreContext);
	const { getLatestChannelId } = useContext(ChannelContext);
	const location = useLocation();
	const { items: products } = useProduct({
		include_products: [product_id],
	});
	const [isLoading, setIsLoading] = useState(false);
	const { showToast } = useToast();
	const { t } = useTranslation('hooks_item_button');

	const product = products[0];
	if (!product) return {};

	const inCart = isItemInCart({
		markers: product?.markers,
		product_id,
		product_settings: product.product_settings,
		product_type: product.type,
	});

	async function handleAction(event) {
		if (isLoading) return;

		event.preventDefault();
		setIsLoading(true);

		const new_event = {
			currency: product?.product_variant?.currency?.code ?? '',
			items: [
				{
					amount: product?.product_variant?.price,
					channel_id: getLatestChannelId(),
					item_list_name: tracking?.item_list_name ?? undefined,
					product_id,
					quantity: 1,
					title: product?.product_variant?.title,
				},
			],
			value: product?.product_variant?.price ?? 0,
		};

		if (inCart.status == 'other') {
			showToast({ title: t('t3') });
		} else if (inCart.status == 'replaced') {
			showToast({ title: t('t4') });
		} else if (inCart.status == 'no') {
			if (!tracking) tracking = {};
			if (!tracking?.attributes) tracking.attributes = {};
			if (!tracking?.attributes?.pathname && location?.pathname) {
				tracking.attributes.url_pathname = location.pathname;
			}
			if (
				!tracking?.attributes?.previous_pathname &&
				location?.state?.previous_pathname &&
				location?.state?.previous_pathname != location?.pathname
			) {
				tracking.attributes.url_previous_pathname =
					location?.state?.previous_pathname;
			}

			const itemResult = await addItem({
				attributes: tracking?.attributes,
				channel_id: getLatestChannelId(),
				product_variant_id: product?.product_variant?.product_variant_id,
				source: tracking?.source,
			});
			if (itemResult?.data?.cart_id) {
				if (itemResult?.data?.action == 'exists') {
					showToast({ title: t('t2') });
				} else if (
					itemResult?.data?.action == 'add' ||
					itemResult?.data?.action == 'replace'
				) {
					showToast({
						count: itemResult?.data?.items?.replace?.length,
						status: itemResult?.data?.action,
						title: product?.product_variant?.title,
						variant: 'add_product',
					});

					if (onAddToCart) onAddToCart();
					createEvent('add_to_cart', new_event);
				}
			}
		} else if (inCart.status == 'yes') {
			const itemResult = await removeItem({
				product_variant_id: product?.product_variant?.product_variant_id,
			});
			if (itemResult?.status == 'ok') {
				createEvent('remove_from_cart', new_event);
			}
		}
		setIsLoading(false);
	}

	return {
		handleAction,
		inCart,
		isLoading,
	};
}

export default useItemButton;
